/**
 * GESTION PRODUITS + PUBLICITÉ — /app/boutique-produits  (admin)
 * Ajoute/édite vos produits (Tatami & autres) avec photo, et génère la pub IA
 * (légendes FB/IG + hashtags + visuel + conseils pub payante + page partageable).
 * Branché sur la VRAIE boutique publique /store (dropship_products).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Megaphone, Copy, X, UploadSimple, CircleNotch,
  ShareNetwork, ArrowSquareOut, Package, Tag, Warning, Truck, EnvelopeSimple,
} from "@phosphor-icons/react";

const RED = "#D90429";
const TYPES = [
  { v: "physical", l: "Produit physique" },
  { v: "course", l: "Cours en ligne" },
  { v: "service", l: "Service" },
  { v: "subscription", l: "Abonnement" },
];

export default function BoutiqueProduitsModule() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | product
  const [promoFor, setPromoFor] = useState(null);
  const [reorderFor, setReorderFor] = useState(null);
  const [onlyNoPhoto, setOnlyNoPhoto] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dropship/products?only_active=false");
      setProducts(Array.isArray(data) ? data : []);
    } catch { toast.error("Erreur de chargement"); }
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function save(form) {
    try {
      if (form.id) await api.put(`/dropship/products/${form.id}`, form);
      else await api.post("/dropship/products", form);
      toast.success("Produit enregistré");
      setEditing(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement"); }
  }
  async function remove(id) {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try { await api.delete(`/dropship/products/${id}`); toast.success("Supprimé"); reload(); }
    catch { toast.error("Erreur de suppression"); }
  }

  const noPhotoCount = products.filter((p) => !p.image_url).length;
  const visible = onlyNoPhoto ? products.filter((p) => !p.image_url) : products;

  // Réappro : produits en rupture (stock<=0) ou stock faible (0<stock<5)
  const reorder = products
    .filter((p) => p.stock != null && p.stock < 5)
    .sort((a, b) => (a.stock ?? 0) - (b.stock ?? 0));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="boutique-produits-module">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
            <Package size={16} weight="bold" style={{ color: RED }} /> Boutique · Produits & Publicité
          </div>
          <h1 className="text-2xl font-bold mt-1">Gérer mes produits</h1>
          <p className="text-sm text-zinc-500 mt-1">Tes produits apparaissent dans la boutique publique <b>/store</b>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {noPhotoCount > 0 && (
            <button onClick={() => setOnlyNoPhoto((v) => !v)} data-testid="filter-no-photo-btn"
              className={`flex items-center gap-2 px-4 py-2.5 font-semibold rounded-md border transition-colors text-sm ${onlyNoPhoto ? "bg-amber-500 text-black border-amber-500" : "border-amber-400 text-amber-600 hover:bg-amber-50"}`}>
              <Package size={16} /> {onlyNoPhoto ? "Voir tous" : `${noPhotoCount} sans photo`}
            </button>
          )}
          <button onClick={() => setEditing("new")} data-testid="add-product-btn"
            className="flex items-center gap-2 text-white px-5 py-2.5 font-semibold rounded-md hover:opacity-90 transition-opacity"
            style={{ background: RED }}>
            <Plus size={18} weight="bold" /> Ajouter un produit
          </button>
        </div>
      </div>

      {!loading && reorder.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="reorder-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              <h2 className="font-bold text-base text-amber-900">À recommander · {reorder.length} produit{reorder.length > 1 ? "s" : ""}</h2>
            </div>
            <span className="text-xs text-amber-700">Rupture ou stock faible (&lt; 5). Pense à réapprovisionner.</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {reorder.map((p) => {
              const out = (p.stock ?? 0) <= 0;
              return (
                <div key={p.id} data-testid={`reorder-card-${p.id}`}
                  className="shrink-0 w-56 bg-white border border-amber-200 rounded-lg overflow-hidden flex flex-col">
                  <div className="h-24 bg-zinc-100 relative">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-zinc-300"><Package size={26} /></div>}
                    <span className={`absolute top-2 right-2 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded flex items-center gap-1 ${out ? "bg-red-600" : "bg-orange-500"}`}>
                      <Warning size={11} weight="fill" /> {out ? "Rupture" : "Stock faible"}
                    </span>
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-bold text-sm leading-tight line-clamp-2">{p.name}</div>
                    <div className={`text-xs mt-1 font-bold ${out ? "text-red-600" : "text-orange-600"}`}>{p.stock} en stock</div>
                    <div className="flex gap-2 mt-2 pt-2 border-t border-zinc-100">
                      <button onClick={() => setReorderFor(p)} data-testid={`reorder-order-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-1 text-white text-xs font-semibold py-1.5 rounded hover:opacity-90" style={{ background: RED }}>
                        <Truck size={14} weight="fill" /> Commander
                      </button>
                      <button onClick={() => setEditing(p)} data-testid={`reorder-restock-${p.id}`}
                        className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50 text-zinc-600" title="Modifier le stock">
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => setPromoFor(p)} data-testid={`reorder-promote-${p.id}`}
                        className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50 text-zinc-600" title="Promouvoir">
                        <Megaphone size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin" style={{ color: RED }} /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-zinc-300 rounded-lg" data-testid="empty-products">
          <Package size={40} className="mx-auto text-zinc-300" />
          <p className="text-zinc-500 mt-3">Aucun produit pour l'instant.</p>
          <button onClick={() => setEditing("new")} className="mt-4 underline text-sm" style={{ color: RED }}>Ajouter ton premier produit Tatami</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {visible.map((p) => (
            <div key={p.id} className="border border-zinc-200 rounded-lg overflow-hidden bg-white flex flex-col" data-testid={`product-card-${p.id}`}>
              <div className="h-44 bg-zinc-100 relative">
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  : <button onClick={() => setEditing(p)} className="w-full h-full flex flex-col items-center justify-center text-zinc-400 hover:bg-zinc-50 transition-colors" data-testid={`add-photo-${p.id}`}>
                      <Package size={34} />
                      <span className="text-[11px] mt-1.5 font-semibold text-amber-600">+ Ajouter une photo</span>
                    </button>}
                {!p.is_active && <span className="absolute top-2 left-2 bg-zinc-900/80 text-white text-[10px] uppercase tracking-wide px-2 py-1 rounded">Inactif</span>}
                {!p.image_url && <span className="absolute bottom-2 left-2 bg-amber-500 text-black text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded">Sans photo</span>}
                {p.brand && <span className="absolute top-2 right-2 bg-white/90 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded flex items-center gap-1"><Tag size={11} />{p.brand}</span>}
                {p.stock != null && p.stock <= 0 && (
                  <span className="absolute bottom-2 right-2 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded flex items-center gap-1" data-testid={`stock-badge-${p.id}`} title="Rupture de stock — à recommander">
                    <Warning size={11} weight="fill" /> Rupture
                  </span>
                )}
                {p.stock != null && p.stock > 0 && p.stock < 5 && (
                  <span className="absolute bottom-2 right-2 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded flex items-center gap-1" data-testid={`stock-badge-${p.id}`} title="Stock faible — pense à recommander">
                    <Warning size={11} weight="fill" /> Stock faible
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-bold text-sm leading-tight">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {p.category || "—"}
                  {p.stock != null && (
                    <span className={p.stock <= 0 ? "text-red-600 font-bold" : p.stock < 5 ? "text-orange-600 font-bold" : ""}>
                      {` · ${p.stock} en stock`}
                    </span>
                  )}
                </div>
                <div className="mt-1.5">
                  {p.beneficiary_entity === "foundation" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300" data-testid={`entity-badge-${p.id}`} title="Verse dans le compte Fondation">
                      ⚠️ Fondation
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded text-white" style={{ background: RED }} data-testid={`entity-badge-${p.id}`} title="Verse dans le compte Impack Boxing Inc.">
                      Impack Boxing Inc.
                    </span>
                  )}
                </div>
                <div className="text-lg font-extrabold mt-2" style={{ color: RED }}>{Number(p.price_cad).toLocaleString("fr-CA")} $</div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100">
                  <button onClick={() => setPromoFor(p)} data-testid={`promote-btn-${p.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2 rounded hover:opacity-90" style={{ background: RED }}>
                    <Megaphone size={14} weight="fill" /> Promouvoir
                  </button>
                  <button onClick={() => setEditing(p)} data-testid={`edit-btn-${p.id}`} className="p-2 border border-zinc-200 rounded hover:bg-zinc-50"><PencilSimple size={16} /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-btn-${p.id}`} className="p-2 border border-zinc-200 rounded hover:bg-red-50 text-red-600"><Trash size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductForm product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} />}
      {promoFor && <PromoPanel product={promoFor} onClose={() => setPromoFor(null)} />}
      {reorderFor && <ReorderModal product={reorderFor} onClose={() => setReorderFor(null)} />}
    </div>
  );
}

function ReorderModal({ product, onClose }) {
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/b2b/reorder-draft", { product_id: product.id });
        setDraft(data);
        setTo(data.to || "");
        setSubject(data.subject || "");
        setBody(data.body || "");
      } catch {
        toast.error("Impossible de préparer le brouillon");
      }
      setLoading(false);
    })();
  }, [product.id]);

  async function createGmailDraft() {
    if (!to.trim()) return toast.error("Destinataire manquant — choisis un fournisseur");
    setSending(true);
    try {
      await api.post("/gmail/draft", { to, subject, body });
      toast.success("Brouillon créé dans Gmail ✉️");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail non connecté — utilise « Ouvrir » ou « Copier »");
    }
    setSending(false);
  }

  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="reorder-modal">
      <div className="bg-white rounded-lg w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-zinc-100">
          <div className="flex items-center gap-2"><Truck size={20} weight="fill" style={{ color: RED }} /><h2 className="font-bold text-lg">Commander : {product.name}</h2></div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10"><CircleNotch size={28} className="animate-spin" style={{ color: RED }} /></div>
          ) : (
            <>
              {draft && !draft.matched && (
                <div className="text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-2" data-testid="reorder-nomatch">
                  Aucun fournisseur B2B associé automatiquement à la marque « {product.brand || "—"} ». Choisis le destinataire ci-dessous ou ajoute-le dans <b>Fournisseurs B2B</b>.
                </div>
              )}
              {draft?.matched && (
                <div className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 rounded px-3 py-2" data-testid="reorder-match">
                  Fournisseur détecté : <b>{draft.supplier.brand}</b>{draft.supplier.contact_name ? ` · ${draft.supplier.contact_name}` : ""}
                </div>
              )}
              {draft?.velocity_note && (
                <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 flex items-start gap-2" data-testid="reorder-velocity">
                  <Tag size={14} weight="fill" className="mt-0.5 shrink-0" />
                  <span>Quantité suggérée : <b>{draft.suggested_quantity} unités</b>. {draft.velocity_note}</span>
                </div>
              )}
              <Field label="Destinataire">
                <input list="reorder-suppliers" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reorder-to" className="inp" placeholder="courriel du fournisseur" />
                <datalist id="reorder-suppliers">
                  {(draft?.suppliers || []).map((s) => <option key={s.contact_email} value={s.contact_email}>{s.brand}</option>)}
                </datalist>
              </Field>
              <Field label="Objet"><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reorder-subject" className="inp" /></Field>
              <Field label="Message"><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} data-testid="reorder-body" className="inp" /></Field>
            </>
          )}
        </div>
        {!loading && (
          <div className="flex flex-wrap gap-2 p-5 border-t border-zinc-100">
            <button onClick={createGmailDraft} disabled={sending} className="flex-1 min-w-[180px] flex items-center justify-center gap-2 text-white py-2.5 rounded font-semibold hover:opacity-90 disabled:opacity-50" style={{ background: RED }} data-testid="reorder-gmail-btn">
              {sending ? <CircleNotch size={16} className="animate-spin" /> : <EnvelopeSimple size={16} weight="fill" />} Créer un brouillon Gmail
            </button>
            <a href={mailto} className="px-4 py-2.5 border border-zinc-200 rounded hover:bg-zinc-50 flex items-center gap-1.5 text-sm" data-testid="reorder-mailto"><ArrowSquareOut size={15} /> Ouvrir</a>
            <button onClick={() => { navigator.clipboard.writeText(`${subject}\n\n${body}`); toast.success("Copié !"); }} className="px-4 py-2.5 border border-zinc-200 rounded hover:bg-zinc-50 flex items-center gap-1.5 text-sm" data-testid="reorder-copy"><Copy size={15} /> Copier</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductForm({ product, onClose, onSave }) {
  const [form, setForm] = useState({
    id: product?.id, name: product?.name || "", type: product?.type || "physical",
    price_cad: product?.price_cad ?? "", description: product?.description || "",
    short_description: product?.short_description || "", category: product?.category || "",
    brand: product?.brand || "", stock: product?.stock ?? "", image_url: product?.image_url || "",
    is_active: product?.is_active ?? true,
    beneficiary_entity: product?.beneficiary_entity || "company",
  });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  async function uploadImage(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/dropship/upload-image", fd);
      set("image_url", data.url);
      toast.success("Image téléversée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du téléversement"); }
    setUploading(false);
  }

  function submit() {
    if (!form.name.trim()) return toast.error("Le nom est obligatoire");
    if (form.price_cad === "" || isNaN(Number(form.price_cad))) return toast.error("Prix invalide");
    if (!form.description.trim()) return toast.error("La description est obligatoire");
    onSave({
      ...form, price_cad: Number(form.price_cad),
      stock: form.stock === "" ? null : Number(form.stock),
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="product-form-modal">
      <div className="bg-white rounded-lg w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-zinc-100">
          <h2 className="font-bold text-lg">{product ? "Modifier le produit" : "Nouveau produit"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Image */}
          <div className="flex gap-4 items-center">
            <div className="w-24 h-24 bg-zinc-100 rounded overflow-hidden shrink-0 flex items-center justify-center">
              {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-cover" /> : <Package size={28} className="text-zinc-300" />}
            </div>
            <div className="flex-1">
              <label className="inline-flex items-center gap-2 cursor-pointer border border-zinc-200 rounded px-3 py-2 text-sm hover:bg-zinc-50" data-testid="upload-image-label">
                {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                {uploading ? "Téléversement…" : "Téléverser une photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="product-image-input" />
              </label>
              <input type="url" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="…ou coller une URL d'image"
                className="w-full mt-2 border border-zinc-200 rounded px-3 py-2 text-sm" data-testid="product-image-url" />
            </div>
          </div>

          <Field label="Nom *"><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className="inp" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prix (CAD) *"><input type="number" step="0.01" value={form.price_cad} onChange={(e) => set("price_cad", e.target.value)} data-testid="pf-price" className="inp" /></Field>
            <Field label="Stock"><input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" className="inp" placeholder="ex: 12" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie"><input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className="inp" placeholder="ex: Gi BJJ" /></Field>
            <Field label="Marque"><input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" className="inp" placeholder="ex: Tatami" /></Field>
          </div>
          <Field label="Type">
            <select value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="pf-type" className="inp">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Description *"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="pf-description" className="inp" /></Field>
          <Field label="Entité bénéficiaire * (compte de versement)">
            <select value={form.beneficiary_entity} onChange={(e) => set("beneficiary_entity", e.target.value)} data-testid="pf-entity" className="inp">
              <option value="company">Impack Boxing Inc. (commercial — recommandé)</option>
              <option value="foundation">Fondation (dons / caritatif seulement)</option>
            </select>
          </Field>
          {form.beneficiary_entity === "foundation" && (
            <div className="text-[12px] bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-2" data-testid="pf-entity-warning">
              ⚠️ Ce produit versera dans le compte de la <b>Fondation</b>. À réserver aux articles réellement caritatifs. L'équipement et les produits commerciaux doivent aller à <b>Impack Boxing Inc.</b>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="pf-active" /> Visible en boutique
          </label>
        </div>
        <div className="flex gap-2 p-5 border-t border-zinc-100">
          <button onClick={submit} className="flex-1 text-white py-2.5 rounded font-semibold hover:opacity-90" style={{ background: RED }} data-testid="save-product-btn">Enregistrer</button>
          <button onClick={onClose} className="px-5 py-2.5 border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
        </div>
      </div>
    </div>
  );
}

function PromoPanel({ product, onClose }) {
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const shareUrl = `${window.location.origin}/promo/${product.slug || product.id}`;

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    api.get(`/dropship/products/${product.id}/promo`).then((r) => setPromo(r.data.promo)).catch(() => {});
  }, [product.id]);

  async function generate() {
    setBusy(true);
    try {
      const { data } = await api.post(`/dropship/products/${product.id}/promo`);
      setPromo(data.promo);
      toast.success("Publicité générée par IA !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de génération"); }
    setBusy(false);
  }
  function copy(text) { navigator.clipboard.writeText(text); toast.success("Copié !"); }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="promo-panel">
      <div className="bg-white rounded-lg w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-zinc-100">
          <div className="flex items-center gap-2"><Megaphone size={20} weight="fill" style={{ color: RED }} /><h2 className="font-bold text-lg">Promouvoir : {product.name}</h2></div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Lien partageable */}
          <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1.5"><ShareNetwork size={13} /> Page promo partageable</div>
            <div className="flex gap-2">
              <input readOnly value={shareUrl} className="flex-1 bg-white border border-zinc-200 rounded px-3 py-2 text-sm" data-testid="promo-share-url" />
              <button onClick={() => copy(shareUrl)} className="px-3 border border-zinc-200 rounded hover:bg-zinc-100" data-testid="promo-copy-share"><Copy size={16} /></button>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="px-3 border border-zinc-200 rounded hover:bg-zinc-100 flex items-center" data-testid="promo-open-share"><ArrowSquareOut size={16} /></a>
            </div>
          </div>

          {!promo ? (
            <div className="text-center py-8">
              <p className="text-zinc-500 text-sm mb-4">Génère une publicité complète (textes + visuel) en un clic avec l'IA.</p>
              <button onClick={generate} disabled={busy} className="inline-flex items-center gap-2 text-white px-6 py-3 rounded font-semibold hover:opacity-90 disabled:opacity-50" style={{ background: RED }} data-testid="generate-promo-btn">
                {busy ? <><CircleNotch size={18} className="animate-spin" /> Génération…</> : <><Megaphone size={18} weight="fill" /> Générer la publicité IA</>}
              </button>
            </div>
          ) : (
            <>
              {promo.image && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Visuel IA</div>
                  <img src={promo.image} alt="Visuel promo" className="w-full rounded-lg border border-zinc-200" data-testid="promo-image" />
                  <a href={promo.image} download={`promo-${product.slug || product.id}.png`} className="inline-flex items-center gap-1.5 text-sm mt-2 underline" style={{ color: RED }}>Télécharger l'image</a>
                </div>
              )}
              <CaptionBlock label="Légende Facebook" text={promo.facebook} onCopy={copy} testid="promo-facebook" />
              <CaptionBlock label="Légende Instagram" text={promo.instagram} onCopy={copy} testid="promo-instagram" />
              {promo.hashtags?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center justify-between">
                    <span>Hashtags</span>
                    <button onClick={() => copy(promo.hashtags.map((h) => `#${h}`).join(" "))} className="text-zinc-400 hover:text-zinc-700"><Copy size={14} /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="promo-hashtags">
                    {promo.hashtags.map((h, i) => <span key={i} className="text-xs bg-zinc-100 px-2 py-1 rounded">#{h}</span>)}
                  </div>
                </div>
              )}
              <PaidAds data={promo.paid_ads} />
              <button onClick={generate} disabled={busy} className="w-full border border-zinc-200 rounded py-2.5 text-sm hover:bg-zinc-50 disabled:opacity-50" data-testid="regenerate-promo-btn">
                {busy ? "Régénération…" : "↻ Régénérer"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CaptionBlock({ label, text, onCopy, testid }) {
  if (!text) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center justify-between">
        <span>{label}</span>
        <button onClick={() => onCopy(text)} className="text-zinc-400 hover:text-zinc-700"><Copy size={14} /></button>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-sm whitespace-pre-wrap" data-testid={testid}>{text}</div>
    </div>
  );
}

function PaidAds({ data }) {
  if (!data) return null;
  let items = [];
  if (Array.isArray(data)) items = data;
  else if (typeof data === "object") items = [data];
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Conseils — Publicité payante</div>
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-2" data-testid="promo-paid-ads">
        {items.length > 0 ? items.map((it, i) => (
          typeof it === "object" ? (
            <div key={i} className="border-b border-amber-100 last:border-0 pb-2 last:pb-0">
              {Object.entries(it).map(([k, v]) => (
                <div key={k}><b className="capitalize">{String(k).replace(/_/g, " ")} :</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
              ))}
            </div>
          ) : <div key={i}>• {String(it)}</div>
        )) : <div className="whitespace-pre-wrap">{String(data)}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
